import type { OrganisationMembership } from '@ghostscreen/contracts';
import { i18n } from '../../i18n/i18n';

type HealthCounts = Pick<OrganisationMembership, 'displayCount' | 'onlineDisplayCount' | 'offlineDisplayCount'>;

export type OrganisationHealthTone = 'live' | 'warning' | 'idle';

export type OrganisationHealth = {
  label: string;
  tone: OrganisationHealthTone;
};

/** "2 locations · 6 screens" */
export function formatOrganisationSummary(
  organisation: Pick<OrganisationMembership, 'locationCount' | 'displayCount'>,
): string {
  return i18n.t('auth:chooseOrganisation.summary', {
    locations: i18n.t('auth:chooseOrganisation.locations', { count: organisation.locationCount }),
    screens: i18n.t('auth:chooseOrganisation.screens', { count: organisation.displayCount }),
  });
}

/** Fleet status for the business picker; offline screens take priority because they need action. */
export function describeOrganisationHealth(counts: HealthCounts): OrganisationHealth {
  const { displayCount, onlineDisplayCount, offlineDisplayCount } = counts;
  if (displayCount === 0) {
    return { label: i18n.t('auth:chooseOrganisation.health.noScreens'), tone: 'idle' };
  }
  if (offlineDisplayCount > 0) {
    return {
      label: i18n.t('auth:chooseOrganisation.health.offline', { offline: offlineDisplayCount }),
      tone: 'warning',
    };
  }
  if (onlineDisplayCount === displayCount) {
    return { label: i18n.t('auth:chooseOrganisation.health.allLive'), tone: 'live' };
  }
  if (onlineDisplayCount > 0) {
    return {
      label: i18n.t('auth:chooseOrganisation.health.partlyLive', {
        online: onlineDisplayCount,
        total: displayCount,
      }),
      tone: 'live',
    };
  }
  return { label: i18n.t('auth:chooseOrganisation.health.notConnected'), tone: 'idle' };
}

/** Caption under the showcase TV: what is playing right now for this business. */
export function describeOrganisationPlayback(
  organisation: Pick<OrganisationMembership, 'name' | 'onlineDisplayCount'>,
): { isLive: boolean; caption: string } {
  const { name, onlineDisplayCount } = organisation;
  if (onlineDisplayCount === 0) {
    return { isLive: false, caption: i18n.t('auth:chooseOrganisation.showcase.notPlaying', { name }) };
  }
  return {
    isLive: true,
    caption: i18n.t('auth:chooseOrganisation.showcase.playing', { name, count: onlineDisplayCount }),
  };
}
